/**
 * DASHBOARD PAGE — Home operativa (nuovo design, port dal mockup): riga KPI
 * cliccabili + "Attività team recenti" (feed dei task recenti, apre la modale
 * task) + due liste (Task per tipo, Contatti per stato marketing).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchPersons, FETCH_ALL_PAGE_SIZE as PERSON_FETCH_ALL } from '../../entities/person/personThunks';
import { fetchBusinesses, FETCH_ALL_PAGE_SIZE as BUSINESS_FETCH_ALL } from '../../entities/business/businessThunks';
import { fetchTasks, FETCH_ALL_PAGE_SIZE as TASK_FETCH_ALL } from '../../entities/task/taskThunks';
import { taskDateBucket } from '../../entities/task/taskStatus';
import { TASK_TYPES } from '../../entities/task/taskTypes';
import { formatDateTime, htmlToPlainTextPreserveBreaks } from '@crm/shared';
import { TaskEditModal } from '../../entities/task/TaskEditModal';
import {
  ContactsGlyph, TargetGlyph, StarGlyph, CompaniesGlyph, CheckSquareGlyph, AlertGlyph,
} from '../../components/icons';
import type { Task } from '../../entities/task/taskTypes';

// marketingStatus (snake, valori backend) → chiave i18n della label (camel).
const MARKETING_LABEL_KEY: Record<string, string> = {
  lead: 'lead',
  marketing_qualified: 'marketingQualified',
  sales_qualified: 'salesQualified',
  customer: 'customer',
};

function initialsOf(name?: string): string {
  if (!name) return '';
  return name.trim().split(/\s+/).slice(0, 2).map((n) => n[0]?.toUpperCase() ?? '').join('');
}

export function DashboardPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { items: persons, status: personsStatus } = useAppSelector((state) => state.persons);
  const { items: businesses, status: businessesStatus } = useAppSelector((state) => state.businesses);
  const { items: tasks, status: tasksStatus } = useAppSelector((state) => state.tasks);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Fetch-all su tutte e tre le entità: i box aggregano sull'intero set.
  useEffect(() => {
    if (personsStatus === 'idle') dispatch(fetchPersons({ pageSize: PERSON_FETCH_ALL }));
    if (businessesStatus === 'idle') dispatch(fetchBusinesses({ pageSize: BUSINESS_FETCH_ALL }));
    if (tasksStatus === 'idle') dispatch(fetchTasks({ pageSize: TASK_FETCH_ALL }));
  }, [personsStatus, businessesStatus, tasksStatus, dispatch]);

  const totalContatti = persons.length;
  const contattiCustomer = persons.filter((p) => p.marketingStatus === 'customer').length;
  const contattiQualified = persons.filter(
    (p) => p.marketingStatus === 'sales_qualified' || p.marketingStatus === 'marketing_qualified',
  ).length;
  const totalAziende = businesses.length;
  const totalTasks = tasks.length;
  // "Scaduti" è uno stato DERIVATO dalle date (il backend persiste solo
  // todo/completed): si conta col bucket, non con t.status === 'expired'.
  const tasksScaduti = tasks.filter((tk) => taskDateBucket(tk) === 'expired').length;

  const recentTasks = [...tasks]
    .sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime())
    .slice(0, 8);

  // Task per tipo (label i18n) e Contatti per stato marketing (label i18n).
  const tasksByType = TASK_TYPES
    .map((tp) => ({ label: t(tp.label), count: tasks.filter((tk) => tk.type === tp.value).length }))
    .filter((r) => r.count > 0);
  const contactsByMarketing = Object.entries(MARKETING_LABEL_KEY)
    .map(([value, key]) => ({
      label: t(`person.marketingStatuses.${key}`),
      count: persons.filter((p) => p.marketingStatus === value).length,
    }))
    .filter((r) => r.count > 0);

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <span className="page-sub">{t('dashboard.sales')}</span>
        </div>
      </div>

      {/* Riga KPI */}
      <div className="kpi-grid">
        <Kpi tone="brand" icon={<ContactsGlyph />} value={totalContatti} label={t('dashboard.totalContacts')} to="/contacts" />
        <Kpi tone="info" icon={<TargetGlyph />} value={contattiQualified} label={t('dashboard.qualifiedContacts')} to="/contacts?box=qualified" />
        <Kpi tone="gold" icon={<StarGlyph />} value={contattiCustomer} label={t('dashboard.customers')} to="/contacts?box=customers" />
        <Kpi tone="company" icon={<CompaniesGlyph />} value={totalAziende} label={t('dashboard.companies')} to="/companies" />
        <Kpi tone="info" icon={<CheckSquareGlyph />} value={totalTasks} label={t('dashboard.totalTasksStat')} to="/tasks" />
        <Kpi tone="crit" icon={<AlertGlyph />} value={tasksScaduti} label={t('dashboard.overdueTasks')} to="/tasks?tab=expired" />
      </div>

      <div className="dash-cols">
        {/* Feed attività recenti */}
        <div className="dcard">
          <div className="dcard__head">
            <h2 className="dcard__title">{t('dashboard.recentActivities')}</h2>
            <span className="dcard__sub">{t('dashboard.totalTasks', { count: totalTasks })}</span>
          </div>
          <div className="feed">
            {recentTasks.length === 0 ? (
              <p className="feed-empty">{t('dashboard.noRecentActivities')}</p>
            ) : (
              recentTasks.map((task) => {
                // Le note del task sono HTML (RichTextEditor). Qui NON si rende
                // l'HTML: la riga è un bottone che apre la modale, e un link o un
                // blocco dentro un elemento cliccabile sarebbe sia inutilizzabile
                // sia sballato come altezza. Si mostra il testo ripulito, come già
                // fa l'activity feed dei dettagli (`useEntityActivities`).
                // `PreserveBreaks` e non `htmlToPlainText` perché i tag di blocco
                // vanno tradotti in uno stacco: senza, "…casissimo.<div>Formattato"
                // diventerebbe "…casissimo.Formattato".
                const notesPreview = task.notes ? htmlToPlainTextPreserveBreaks(task.notes) : '';
                return (
                  <button
                    key={task.taskId}
                    type="button"
                    className="feed-item"
                    onClick={() => setSelectedTask(task)}
                  >
                    <span className="fav">{initialsOf(task.userName)}</span>
                    <div className="f-main">
                      <div className="f-line">
                        <b>{task.userName}</b>{' '}
                        {t(`dashboard.activityLabels.${task.type}`, { defaultValue: task.type })}
                      </div>
                      <div className="f-title">{task.title}</div>
                      {/* Il controllo è sul testo estratto, non su `task.notes`: una
                          nota svuotata dall'editor resta "<p></p>", stringa non vuota
                          che produrrebbe una riga alta e bianca. `activity-notes-preview`
                          ferma l'anteprima a due righe, altrimenti una nota lunga
                          spinge fuori schermo le attività successive. */}
                      {notesPreview && (
                        <div className="f-note activity-notes-preview">{notesPreview}</div>
                      )}
                      <div className="f-date">{formatDateTime(task.createDate)}</div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Liste riepilogo */}
        <div>
          <div className="dcard">
            <div className="dcard__head"><h2 className="dcard__title">{t('dashboard.tasksByType')}</h2></div>
            <div className="wlist">
              {tasksByType.length === 0 ? (
                <p className="wlist-empty">—</p>
              ) : (
                tasksByType.map((r) => (
                  <div key={r.label} className="wrow"><span className="wl">{r.label}</span><span className="wc">{r.count}</span></div>
                ))
              )}
            </div>
          </div>
          <div className="dcard">
            <div className="dcard__head"><h2 className="dcard__title">{t('dashboard.contactsByMarketing')}</h2></div>
            <div className="wlist">
              {contactsByMarketing.length === 0 ? (
                <p className="wlist-empty">—</p>
              ) : (
                contactsByMarketing.map((r) => (
                  <div key={r.label} className="wrow"><span className="wl">{r.label}</span><span className="wc">{r.count}</span></div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedTask && (
        <TaskEditModal task={selectedTask} onClose={() => setSelectedTask(null)} />
      )}
    </div>
  );
}

function Kpi({
  tone, icon, value, label, to,
}: {
  tone: 'brand' | 'info' | 'gold' | 'company' | 'crit';
  icon: ReactNode;
  value: number;
  label: string;
  to: string;
}) {
  return (
    <Link to={to} className={`kpi kpi--${tone}`}>
      <span className="kpi__icon">{icon}</span>
      <span className="kpi__val">{value.toLocaleString()}</span>
      <span className="kpi__label">{label}</span>
    </Link>
  );
}
